import { Box } from "@mui/material";
import React, { useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import ServiceIcon from "../../../../../svg/ServiceIcon";
import { formatClockTime } from "../../helpers/activitySelectors";
import { ScheduledService } from "../../model/workoutPlanTypes";

type Props = {
  service: ScheduledService;
  /** Drawn with the primary border while its details are shown beside the week. */
  selected?: boolean;
  onOpen: (service: ScheduledService) => void;
};

/**
 * A coached session on a day, as the mobile `ServiceCard` shows it: the
 * service's thumbnail (or the platform service glyph), its type name and the
 * start time with the duration. Past sessions are muted. Picking it selects
 * it for the details pane.
 */
const ServiceCard = ({ service, selected = false, onOpen }: Props) => {
  const theme: Theme = useTheme();
  const [imageFailed, setImageFailed] = useState(false);
  const isPast = new Date(service.endTime).getTime() < Date.now();
  const showImage = !!service.image_small_url && !imageFailed;

  return (
    <Box
      sx={styles.card(theme, isPast, selected)}
      onClick={() => onOpen(service)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(service);
        }
      }}
    >
      <Box sx={styles.titleRow}>
        {showImage ? (
          <Box
            component="img"
            src={service.image_small_url}
            alt=""
            onError={() => setImageFailed(true)}
            sx={styles.thumb}
          />
        ) : (
          <Box sx={styles.iconDisc(theme)}>
            <ServiceIcon fill={theme.colors_ds.NEUTRAL_TEXT_STRONG} />
          </Box>
        )}
        <Box sx={styles.title(theme)} title={service.service_type_name}>
          {service.service_type_name}
        </Box>
      </Box>
      <Box sx={styles.meta(theme)}>
        {formatClockTime(service.startTime)} - {service.duration} min
      </Box>
    </Box>
  );
};

export default ServiceCard;

const styles = {
  card: (theme: Theme, isPast: boolean, selected: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${
      selected ? theme.colorTokens.P[60] : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: selected ? theme.colorTokens.P[10] : theme.colors_ds.NEUTRAL_BG_MAIN,
    opacity: isPast ? 0.6 : 1,
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    "&:hover": { borderColor: theme.colorTokens.P[60] },
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
    },
  }),
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
  thumb: {
    flexShrink: 0,
    width: "32px",
    height: "32px",
    borderRadius: "16px",
    objectFit: "cover" as "cover",
  },
  iconDisc: (theme: Theme) => ({
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  title: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  meta: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
